import { reactive, toRaw } from "vue";
import { Modal } from "ant-design-vue";
import store from "../../../../../store";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import i18n from "../../../../../i18n";
import {
    createColumnBoolean,
    createColumnInlineSelect,
    createColumnInput,
    createColumnSelect
} from "../../../../../components/table/c-table.logic";
import { getLabTypeByKey, getLabTypeKeyByRoleMenuAttributes } from "../lab-share.logic";
import { onClickView } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { required, validateError } from "../../../../../utils/validate";
import { createManageExportCsv, createManageSelect, createPage } from "../../../../../utils/page";
import lodash from "lodash";
import { createFieldSelect } from "../../../../../components/input/template-builder/template-builder.logic";
import { useForm as createUseForm } from "@ant-design-vue/use";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/product";
export const routerPrefix = "product-grouping";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const callSaveAllProductGroupRel = ({data}) => store.dispatch(`resource/v1/master/labProductGroupRel/saveAll`, {data});
export const callDeleteAllProductGroupRel = ({ids}) => store.dispatch(`resource/v1/master/labProductGroupRel/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callLabProductGroups = (searchQuery = "", productTypeId, labType) => {
    let where = [
        { name: "refProductType.id", type: "UUID", operator: "=", value: productTypeId },
        { name: "refLabType.key", type: "String", operator: "=", value: labType }
    ];

    if (searchQuery) {
        where = [
            ...where,
            { name: "name", type: "String", operator: "like", value: searchQuery }
        ];
    }

    return store.dispatch("resource/v1/master/labProductGroup/search", { criteria: { select: ["**"], where: where } })
        .then(response => {return response.data;});
};

export const callProductType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PRODUCT_TYPE", value: searchQuery });
export const callLookupByGroupName = ({ group, searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group, value: searchQuery });

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onSearch = ({ page, data }) => {
    setIsLoading(true);
    const { where = [] } = page;

    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            select: ["**"],
            where: [...where],
            data: [],
            children: [
                {
                    name: "labProductGroupRels",
                    select: ['**'],
                    page: 0,
                    size: 1,
                    where: [{ name: "refLabProductGroup.refLabType.key", type: "String", operator: '=', value: data.labType.key }],
                    sort: [{ name: "updateDate", type: "DESC" }],
                }
            ]
        }
    })
        .then(page => {
            if (!page?.sort?.length) {
                page.data = lodash.sortBy(page.data, d => {
                    const labProductGroupName = d.labProductGroupRels?.[0]?.refLabProductGroup?.name
                    return !labProductGroupName ? `0${d.code}` : `${labProductGroupName}${d.code}`;
                })
            }
            state.page = { ...page }
        })
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const onClickSaveAllProductGroupRel = ({ page, data }) => {
    Modal.confirm({
        title: i18n.global.t('components.table.operatorAndPagination.youAreWant', {text: i18n.global.t('button.save')}),
        autoFocusButton: 'cancel',
        onCancel: () => {
            setIsLoadingApp(false)
        },
        onOk: () => {
            setIsLoadingApp(true)
            const dataIsSave = lodash.cloneDeep(page?.data).filter(d=>d.isSave)
            dataIsSave.map(product=>product.labProductGroupRels.map(rels => rels.refProduct = { id: product.id }))
            const rels = dataIsSave.flatMap(d=>d.labProductGroupRels)
            const relsIsSave = rels.filter(d=>d.isSave && !d.isDelete)
            const relsIsDeleteIds = rels.filter(d=>d.id && d.isDelete).map(d=>d.id)
            const promise = []
            if (relsIsSave.length > 0) { promise.push(callSaveAllProductGroupRel({ data: relsIsSave })) }
            if (relsIsDeleteIds.length > 0) { promise.push(callDeleteAllProductGroupRel({ ids: relsIsDeleteIds })) }
            return Promise.all(promise)
                .finally(() => {
                    setIsLoadingApp(false)
                    onSearch({ page: state.page, data })
                })
        },
    });
};

export const createColumns = ({ data }) => {
    const { useForm } = data
    const columns = [
        createColumnBoolean({
            title: () => i18n.global.t("components.table.column.isSave"),
            dataIndex: "isSave",
            sorter: false,
            search: { visible: () => false }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.product.code"),
            dataIndex: "code",
        }),
        createColumnInput({
            title: () => i18n.global.t("model.product.name"),
            dataIndex: "nameTh",
        }),
        createColumnSelect({
            //visible: () => false,
            title: () => i18n.global.t("model.product.refProductType"),
            dataIndex: "refProductType.value",
            customRender: ({ record }) => <div>{record.refProductType.valueEn} - {record.refProductType.value}</div>,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => {
                        field.searchValue = value
                        data.searchProductType = value
                    },
                    list: ({ searchQuery }) => callLookupByGroupName({ group: "PRODUCT_TYPE", searchQuery }),
                    renderLabel: ({ item }) => <div>{item.valueEn} - {item.value}</div>,
                    allowClear: false,
                };
            },
            width: 180,
            search: {
                field: {
                    searchValue: data.searchProductType,
                    rulesRef: [required]
                },
                type: "String",
                operator: "=",
                getValue: ({ value }) => value.value
            },
        }),
        createColumnInlineSelect({
            title: () => i18n.global.t("model.labProductGrouping.form.refLabProductGroup"),
            dataIndex: "refLabProductGroupTemp.name",
            items: state.page?.data || [],
            useForm,
            itemsKey: "labProductGroupRels",
            inputProp: () => ({
                list: ({ searchQuery }) => callLabProductGroups(searchQuery, data.searchProductType?.id, data.labType?.key)
            }),
            customRenderInputProp: ({ record }) => {
                const labProductGroupRels = record.labProductGroupRels;
                const rel = labProductGroupRels[0];
                record.refLabProductGroupTemp = rel?.refLabProductGroup;
                return {
                    value: rel?.refLabProductGroup,
                    "onUpdate:value": value => {
                        record.refLabProductGroupTemp = value;
                        record.isSave = true;
                        if (rel) {
                            rel.refLabProductGroup = value;
                            rel.isSave = true;
                            if (!value) {
                                rel.isDelete = true
                            } else {
                                rel.isDelete = false
                            }
                        } else {
                            labProductGroupRels.push({ refLabProductGroup: value, isSave: true })
                        }
                    },
                    list: ({ searchQuery }) => callLabProductGroups(searchQuery, data.searchProductType?.id, data.labType?.key),
                    optLabel: "name"
                }
            },
            search: {
                type: "String",
                operator: "=",
                getValue: ({ value }) => value.name
            },
            sorter: false,
            width: 200,
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticType"),
            dataIndex: "refAquaticType.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: ({ searchQuery }) => callLookupByGroupName({ group: "AQUATIC_TYPE", searchQuery }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticSource"),
            dataIndex: "refAquaticSource.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: ({ searchQuery }) => callLookupByGroupName({ group: "AQUATIC_SOURCE", searchQuery }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductMethod"),
            dataIndex: "refProductMethod.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: () => callLookupByGroupName({ group: "PRODUCT_METHOD" }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticSpecies"),
            dataIndex: "refAquaticSpecies.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: ({ searchQuery }) => callLookupByGroupName({ group: "AQUATIC_SPECIES", searchQuery }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductFeture"),
            dataIndex: "refProductFeture.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: ({ searchQuery }) => callLookupByGroupName({ group: "PRODUCT_FEATURE", searchQuery }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductConsumption"),
            dataIndex: "refProductConsumption.value",
            visible: () => false,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: () => callLookupByGroupName({ group: "PRODUCT_CONSUMPTION" }),
                    optLabel: "value"
                };
            },
            search: { visible: () => false },
            align: "center"
        })
    ];
    return columns;
};

export const createForm = ({ data }) => {
    return {
        labType: data.labType,
    }
}

export const createRules = () => {
    return {
        [`labType`]: [required],
    }
}

export const createField = ({ data }) => {
    const useForm = data.useForm
    const modelRef = useForm.modelRef
    modelRef.labType = data.labType
    return [
        createFieldSelect({
            visible: () => data.labAttributes?.length > 1,
            key: 'labType',
            colProp: () => ({ xs: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.lookup.modelGroup.LAB_TYPE'),
            inputProp: () => ({
                value: data.labType,
                'onUpdate:value': value => {
                    modelRef.labType = value
                    data.labType = value
                    onSearch({ page: state.page, data })
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_TYPE',
                    value: searchQuery,
                    key: data.labAttributes, keyOperator: 'in', valueLogic: 'AND'
                }),
                optLabel: "value",
                allowClear: false
            }),
        }),
    ]
}

export const initState = async ({ props, data, refs }) => {
    const menuContext = props.menuContext;
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    const labTypeKeys = await getLabTypeByKey({ searchQuery: data.labAttributes[0] });
    const productTypes = await callProductType({ })
    data.labType = labTypeKeys[0];
    data.searchProductType = productTypes[0];
    data.form = createForm({ props, data, refs, isLoading });
    data.rules = createRules({ props, data, refs, isLoading });
    data.useForm =  createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });

    state.columns = createColumns({ props, data });
    state.manages = [
        createManageSelect({ menuContext }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [];
    state.rowActions = [];
};

export const state = reactive({
    columns: [],
    page: createPage({
        options: {
            select: ["**"]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } }
});
